/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */

import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Functions } from '@angular/fire/functions';

import { FirebaseCloudFunctionsService } from './cloud-functions.service';
import {
	IProjectMemberValidationResponse,
	IProjectMemberValidation,
	IRemoveUserProject,
	IRemoveUserProjectResponse,
	ICloudFunctionProjectMember,
	IAddUserToProjectResponse,
	IAddUserProject,
	IAddUserRallyPointResponse,
	IAddUserRallyPoint,
	ICloneProjectParameters,
	ICloneProjectResponse,
	IInviteUserToProject,
} from '@touchplan/common';

@Injectable({
	providedIn: 'root',
})
export class ProjectFirebaseCloudFunctionsService {
	private functionsInstance: Functions = inject(Functions);

	/**
	 * Removes a user from a project.
	 *
	 * @param removeUserParams - Parameters for removing the user from the project.
	 * @returns An observable that emits the response from the server.
	 */
	public removeUserFromProject(removeUserParams: IRemoveUserProject): Observable<IRemoveUserProjectResponse> {
		return FirebaseCloudFunctionsService.callFunctionAsObservable$<
			IRemoveUserProject,
			IRemoveUserProjectResponse
		>(this.functionsInstance, 'mb4-removeUserFromProject', removeUserParams);
	}

	/**
	 * Adds a user to a project.
	 *
	 * @param projectId - The ID of the project.
	 * @param userData - Information about the user being added.
	 * @param email - The email of the user being added.
	 * @returns An observable that emits the response from the server.
	 */
	public addUserToProject(
		projectId: string,
		userData: ICloudFunctionProjectMember,
		email: string
	): Observable<IAddUserToProjectResponse> {
		return FirebaseCloudFunctionsService.callFunctionAsObservable$<
			IAddUserProject,
			IAddUserToProjectResponse
		>(this.functionsInstance, 'mb4-addUserToProject', { projectId, userData, email });
	}

	/**
	 * Adds a user to a project using a Rally Point access code.
	 *
	 * @param userId - The ID of the user being added.
	 * @param accessCode - The access code for the Rally Point.
	 * @returns An observable that emits the response from the server.
	 */
	public addUserToProjectViaRallyPoint(
		userId: string,
		accessCode: string
	): Observable<IAddUserRallyPointResponse> {
		return FirebaseCloudFunctionsService.callFunctionAsObservable$<
			IAddUserRallyPoint,
			IAddUserRallyPointResponse
		>(this.functionsInstance, 'mb4-addUserToProjectViaRallyPoint', { userId, accessCode });
	}

	/**
	 * Validates and repairs membership for a user in a project.
	 *
	 * @param userId - The ID of the user for whom membership is being validated and repaired.
	 * @returns An observable that emits the response from the server.
	 */
	public validateAndRepairMembership(userId: string): Observable<IProjectMemberValidationResponse> {
		return FirebaseCloudFunctionsService.callFunctionAsObservable$<
			IProjectMemberValidation,
			IProjectMemberValidationResponse
		>(this.functionsInstance, 'mb4-validateAndRepairMembership', { userId });
	}

	/**
	 * Clones a project with specified parameters.
	 *
	 * @param cloneProjectParams - Parameters for cloning the project.
	 * @returns An observable that emits the response from the server.
	 */
	public cloneProject(cloneProjectParams: ICloneProjectParameters) {
		return FirebaseCloudFunctionsService.callFunctionAsObservable$<
			ICloneProjectParameters,
			ICloneProjectResponse
		>(this.functionsInstance, 'mb4-cloneProject', cloneProjectParams);
	}

	/**
	 * Updates the information for an invited user in a project.
	 *
	 * @param projectId - The ID of the project where the invited user's information will be updated.
	 * @param userData - Updated information for the invited user.
	 * @param email - The email of the invited user.
	 * TODO: Verify and handle the response appropriately.
	 */
	public updateInvitedUser(
		projectId: string,
		userData: ICloudFunctionProjectMember,
		email: string
	): Observable<void> {
		return FirebaseCloudFunctionsService.callFunctionAsObservable$<IInviteUserToProject, void>(
			this.functionsInstance,
			'mb4-updateInvitedUser',
			{
				projectId,
				userData,
				email,
			}
		);
	}
}
